import React from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Tooltip } from './Tooltip';

/**
 * Um modo de visualização do toggle.
 */
export interface ViewToggleMode<T extends string = string> {
  /** Identificador do modo (ex: 'table', 'grid', 'kanban') */
  id: T;
  /** Ícone 14px via <Icon> (ex: 'list', 'grid') */
  icon: IconName;
  /** Rótulo visível (ex: 'Lista', 'Cards') */
  label: string;
  /**
   * Texto do tooltip. Ausente = usa o `label`. Serve para explicar o modo
   * quando o rótulo não basta — ex.: "Detalhes — mais informação por card"
   * no seletor de tamanho do kanban.
   */
  tooltip?: string;
}

export interface ViewToggleProps<T extends string = string> {
  /** Modos na ordem de exibição (a ordem é do caller — ver convenção no JSDoc do componente) */
  modes: Array<ViewToggleMode<T>>;
  /** Modo ativo */
  value: T;
  /** Callback ao trocar de modo */
  onChange: (id: T) => void;
  /**
   * Colapso do rótulo (padrão do DS 2026-08-04): `hidden 2xl:@tb:inline` — a
   * legenda só aparece em viewport ≥2xl (1536px) E toolbar-container com
   * espaço (≥ token `tb`, 68rem). Requer um ancestral `@container` (o
   * PageToolbar já aplica); sem ancestral container a query avalia FALSE e a
   * legenda fica só-ícone (comportamento desejado em painéis estreitos, ex.
   * Chat). `false` = rótulo sempre visível. Default: true.
   */
  collapsibleLabels?: boolean;
  /** Classe extra no wrapper (pass-through). */
  className?: string;
  /** Base dos testids: container recebe o valor; botões recebem `${base}-${id}` */
  'data-testid'?: string;
}

/**
 * Toggle de modo de visualização (pill neutra) — fonte-da-verdade dos findings
 * F09/F10/F11 do audit de consistência de UI (2026-07-25).
 *
 * Visual vencedor (família de 24 telas, ex: UsersPage/LeadsListPage):
 * container `flex gap-0.5 p-0.5 rounded-lg` fundo `var(--theme-page-background)`
 * + borda `var(--theme-header-border)`; botão `h-8 px-2.5 rounded-md text-[13px]
 * font-semibold` (38px totais — padrão control_height_38); ativo = fundo `var(--theme-card-background)` + cor
 * `var(--theme-text)` (sem cor de destaque); inativo = `var(--theme-muted-text)`;
 * ícone 14px (`w-3.5 h-3.5`).
 *
 * Convenção de ordem/rótulo (F10 — decisão do dono): Lista/Tabela SEMPRE
 * primeiro e o modo grade SEMPRE rotulado "Cards".
 *
 * Tooltip: `Tooltip` do DS (nunca `title` nativo), abrindo para BAIXO — o
 * toggle vive na toolbar do topo. Texto = `mode.tooltip ?? mode.label`.
 *
 * Colapso (padrão do DS 2026-08-04): a legenda usa `hidden 2xl:@tb:inline` —
 * visível só em viewport ≥2xl E toolbar-container ≥ token `tb` (68rem, ver
 * tailwind.config.js). O antigo mecanismo de ganchos por cadeia
 * (`ds-seg`/`ds-dens` em index.css) foi aposentado para legendas.
 *
 * @example
 * ```tsx
 * <ViewToggle
 *   modes={[
 *     { id: 'table', icon: 'list', label: 'Lista' },
 *     { id: 'grid', icon: 'grid', label: 'Cards' },
 *   ]}
 *   value={viewMode}
 *   onChange={setViewMode}
 *   data-testid="users-view"
 * />
 * ```
 */
export function ViewToggle<T extends string = string>({
  modes,
  value,
  onChange,
  collapsibleLabels = true,
  className,
  'data-testid': dataTestId = 'view-toggle',
}: ViewToggleProps<T>): React.ReactElement {
  return (
    <div
      className={clsx(className, 'flex gap-0.5 p-0.5 rounded-lg flex-none')}
      style={{
        backgroundColor: 'var(--theme-page-background)',
        border: '1px solid var(--theme-header-border)',
      }}
      role="group"
      data-testid={dataTestId}
    >
      {modes.map((mode) => {
        const active = value === mode.id;
        return (
          /* Tooltip no lugar do `title` nativo (regra travada do DS). `side="bottom"`
             porque o toggle vive na toolbar do topo — regra do flip por posição. */
          <Tooltip key={mode.id} content={mode.tooltip ?? mode.label} side="bottom">
            <button
              type="button"
              onClick={() => onChange(mode.id)}
              className="flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] font-semibold transition-colors"
              style={
                active
                  ? { backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-text)' }
                  : { color: 'var(--theme-muted-text)' }
              }
              aria-pressed={active}
              data-testid={`${dataTestId}-${mode.id}`}
            >
              <Icon name={mode.icon} className="w-3.5 h-3.5" />
              <span className={collapsibleLabels ? 'hidden 2xl:@tb:inline' : undefined}>{mode.label}</span>
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
